export type TokenType =
  | 'text'
  | 'heading'
  | 'paragraph'
  | 'listItem'
  | 'codeBlock'
  | 'code'
  | 'bold'
  | 'italic'
  | 'link'
  | 'blockquote'
  | 'hr'
  | 'table'
  | 'tableRow'
  | 'tableCell'
  | 'openTag'
  | 'closeTag'
  | 'selfClosingTag'
  | 'latex'
  | 'newline'
  | 'eof'
  | 'alignment'
  | 'float';

export interface Token {
  type: TokenType;
  content?: string;
  name?: string;
  attrs?: Record<string, string>;
  level?: number;
  ordered?: boolean;
  language?: string;
  href?: string;
  title?: string;
  align?: 'left' | 'center' | 'right' | 'justify';
  width?: number;
  indent?: number;
}

export class Tokenizer {
  private pos = 0;
  private source = '';
  private lineStart = 0;
  private lineNumber = 1;
  private stack: string[] = [];
  private inBlock = false;

  tokenize(source: string): Token[] {
    this.source = source;
    this.pos = 0;
    this.lineStart = 0;
    this.lineNumber = 1;
    this.stack = [];
    this.inBlock = false;

    const tokens: Token[] = [];

    while (this.pos < this.source.length) {
      const token = this.nextToken();
      if (
        token.type !== 'newline' ||
        tokens.length === 0 ||
        tokens[tokens.length - 1].type !== 'newline'
      ) {
        tokens.push(token);
      }
    }

    if (tokens.length > 0 && tokens[tokens.length - 1].type !== 'eof') {
      tokens.push({ type: 'eof' });
    }

    return tokens;
  }

  private nextToken(): Token {
    if (this.pos >= this.source.length) {
      return { type: 'eof' };
    }

    const char = this.source[this.pos];

    if (char === '\n' || char === '\r') {
      return this.handleNewline();
    }

    if (this.inBlock && char !== '<') {
      return this.handleTextUntilNewline();
    }

    if (char === '#') {
      return this.handleHeading();
    }

    if (char === '-' || char === '*' || char === '+') {
      if (this.isListStart()) {
        return this.handleListItem();
      }
    }

    if (char === ' ' || char === '\t') {
      if (this.isListStart()) {
        return this.handleListItem();
      }
      // Indented ordered list item: "  1. text" — check for N. after leading whitespace
      if (this.isOrderedListStart()) {
        return this.handleOrderedListItem();
      }
    }

    if (/[0-9]/.test(char)) {
      if (this.isOrderedListStart()) {
        return this.handleOrderedListItem();
      }
    }

    if (char === '>') {
      return this.handleBlockquote();
    }

    if (char === '`') {
      return this.handleCode();
    }

    if (char === '-') {
      return this.handleHr();
    }

    if (char === '|') {
      return this.handleTable();
    }

    if (char === '<') {
      return this.handleXmlTag();
    }

    if (char === '$') {
      return this.handleLatex();
    }

    if (char === ':' && this.source.slice(this.pos, this.pos + 3) === ':::') {
      return this.handleFencedBlock();
    }

    return this.handleText();
  }

  private handleNewline(): Token {
    const start = this.pos;
    if (this.source[this.pos] === '\r') {
      this.pos++;
      if (this.pos < this.source.length && this.source[this.pos] === '\n') {
        this.pos++;
      }
    } else {
      this.pos++;
      if (this.pos < this.source.length && this.source[this.pos] === '\n') {
        this.pos++;
      }
    }

    this.lineNumber++;
    this.lineStart = this.pos;

    this.inBlock = false;

    return { type: 'newline', content: '\n\n' };
  }

  private handleHeading(): Token {
    const start = this.pos;
    let level = 0;

    while (this.pos < this.source.length && this.source[this.pos] === '#') {
      level++;
      this.pos++;
    }

    if (level > 6) {
      this.pos = start;
      return this.handleText();
    }

    while (this.pos < this.source.length && this.source[this.pos] === ' ') {
      this.pos++;
    }

    const content = this.readUntilNewline();

    this.inBlock = true;

    return { type: 'heading', level, content: content.trim() };
  }

  private isListStart(): boolean {
    const saved = this.pos;
    let found = false;

    while (this.pos < this.source.length && /[\s]/.test(this.source[this.pos])) {
      this.pos++;
    }

    if (this.pos < this.source.length && /[\-*+]/.test(this.source[this.pos])) {
      this.pos++;
      if (this.pos < this.source.length && this.source[this.pos] === ' ') {
        this.pos++;
        found =
          this.pos < this.source.length &&
          this.source[this.pos] !== '\n' &&
          this.source[this.pos] !== '\r';
      }
    }

    this.pos = saved;
    return found;
  }

  private isOrderedListStart(): boolean {
    const saved = this.pos;
    let found = false;

    // Skip leading whitespace (handles indented ordered items like "   1. text")
    while (this.pos < this.source.length && /[ \t]/.test(this.source[this.pos])) {
      this.pos++;
    }

    while (this.pos < this.source.length && /[0-9]/.test(this.source[this.pos])) {
      this.pos++;
    }

    if (this.pos < this.source.length && this.source[this.pos] === '.') {
      this.pos++;
      while (this.pos < this.source.length && this.source[this.pos] === ' ') {
        this.pos++;
      }
      found =
        this.pos < this.source.length &&
        this.source[this.pos] !== '\n' &&
        this.source[this.pos] !== '\r';
    }

    this.pos = saved;
    return found;
  }

  private handleListItem(): Token {
    const lineStart = this.pos;

    let spacesBeforeBullet = 0;
    while (this.pos < this.source.length && this.source[this.pos] === ' ') {
      this.pos++;
      spacesBeforeBullet++;
    }

    const bulletChar = this.source[this.pos];
    this.pos++;

    let spacesAfterBullet = 0;
    while (this.pos < this.source.length && this.source[this.pos] === ' ') {
      this.pos++;
      spacesAfterBullet++;
    }

    const indent = spacesBeforeBullet;

    const content = this.readUntilNewline();
    this.inBlock = true;

    return { type: 'listItem', content: content.trim(), ordered: false, indent };
  }

  private handleOrderedListItem(): Token {
    const lineStart = this.pos;

    let spacesBeforeBullet = 0;
    while (this.pos < this.source.length && this.source[this.pos] === ' ') {
      this.pos++;
      spacesBeforeBullet++;
    }

    const bulletStart = this.pos;
    while (this.pos < this.source.length && /[0-9]/.test(this.source[this.pos])) {
      this.pos++;
    }

    if (this.source[this.pos] === '.') {
      this.pos++;
    }

    let spacesAfterBullet = 0;
    while (this.pos < this.source.length && this.source[this.pos] === ' ') {
      this.pos++;
      spacesAfterBullet++;
    }

    const indent = spacesBeforeBullet;

    const content = this.readUntilNewline();
    this.inBlock = true;

    return { type: 'listItem', content: content.trim(), ordered: true, indent };
  }

  private handleBlockquote(): Token {
    this.pos++;
    while (this.pos < this.source.length && this.source[this.pos] === ' ') {
      this.pos++;
    }

    const content = this.readUntilNewline();
    this.inBlock = true;

    return { type: 'blockquote', content: content.trim() };
  }

  private handleCode(): Token {
    const start = this.pos;
    let backticks = 0;

    while (this.pos < this.source.length && this.source[this.pos] === '`') {
      backticks++;
      this.pos++;
    }

    if (backticks >= 3) {
      // Read language (optional) - everything until newline
      let language = '';
      while (
        this.pos < this.source.length &&
        this.source[this.pos] !== '\n' &&
        this.source[this.pos] !== '\r'
      ) {
        language += this.source[this.pos];
        this.pos++;
      }
      language = language.trim();

      // Skip newline
      if (
        this.pos < this.source.length &&
        (this.source[this.pos] === '\n' || this.source[this.pos] === '\r')
      ) {
        this.pos++;
        if (
          this.pos < this.source.length &&
          this.source[this.pos] === '\n' &&
          this.source[this.pos - 1] === '\r'
        ) {
          this.pos++;
        }
      }

      // Read content until we find closing backticks at start of line
      let content = '';
      const closingMarker = '`'.repeat(backticks);

      while (this.pos < this.source.length) {
        // Check if we're at the start of a line with closing backticks
        if (
          this.pos > 0 &&
          (this.source[this.pos - 1] === '\n' || this.source[this.pos - 1] === '\r')
        ) {
          if (this.source.slice(this.pos, this.pos + closingMarker.length) === closingMarker) {
            // Found closing backticks
            this.pos += closingMarker.length;
            // Skip optional whitespace after closing backticks
            while (
              this.pos < this.source.length &&
              (this.source[this.pos] === ' ' || this.source[this.pos] === '\t')
            ) {
              this.pos++;
            }
            break;
          }
        }

        content += this.source[this.pos];
        this.pos++;
      }

      return { type: 'codeBlock', language, content: content.trim() };
    }

    const content = this.readUntil('`');
    this.pos++;

    return { type: 'code', content };
  }

  private handleHr(): Token {
    let dashes = 0;
    while (
      this.pos < this.source.length &&
      (this.source[this.pos] === '-' ||
        this.source[this.pos] === '*' ||
        this.source[this.pos] === '_')
    ) {
      dashes++;
      this.pos++;
    }

    while (this.pos < this.source.length && this.source[this.pos] === ' ') {
      this.pos++;
    }

    if (dashes >= 3) {
      return { type: 'hr' };
    }

    this.pos -= dashes;
    return this.handleText();
  }

  private handleTable(): Token {
    const line = this.readUntilNewline();

    // Check if it's a table row (including separator rows)
    const cells = line.split('|').filter((c) => c.trim());
    const content = line;

    return { type: 'tableRow', content };
  }

  private handleXmlTag(): Token {
    if (this.source.slice(this.pos, this.pos + 4) === '<!--') {
      return this.handleComment();
    }

    const result = this.parseXmlTag();

    if (!result) {
      return this.handleText();
    }

    if (result.isClosing) {
      this.stack.pop();
      return { type: 'closeTag', name: result.name };
    }

    if (result.isSelfClosing) {
      return { type: 'selfClosingTag', name: result.name, attrs: result.attrs };
    }

    this.stack.push(result.name);
    return { type: 'openTag', name: result.name, attrs: result.attrs, content: result.content };
  }

  private handleComment(): Token {
    const end = this.source.indexOf('-->', this.pos);
    if (end === -1) {
      this.pos = this.source.length;
      return { type: 'text', content: '<!--' };
    }
    this.pos = end + 3;
    return { type: 'text', content: '' };
  }

  private parseXmlTag(): {
    name: string;
    attrs: Record<string, string>;
    content?: string;
    isClosing: boolean;
    isSelfClosing: boolean;
  } | null {
    if (this.source[this.pos] !== '<') return null;

    let pos = this.pos + 1;

    const isClosing = this.source[pos] === '/';
    if (isClosing) pos++;

    const nameStart = pos;
    while (pos < this.source.length && /[\w\-:]/.test(this.source[pos])) {
      pos++;
    }
    const name = this.source.slice(nameStart, pos);

    if (!name) return null;

    const attrs: Record<string, string> = {};

    while (pos < this.source.length && this.source[pos] !== '>' && this.source[pos] !== '/') {
      if (/\s/.test(this.source[pos])) {
        pos++;
        continue;
      }

      const attrNameStart = pos;
      while (pos < this.source.length && /[\w\-:]/.test(this.source[pos])) {
        pos++;
      }
      const attrName = this.source.slice(attrNameStart, pos);

      while (pos < this.source.length && /\s/.test(this.source[pos])) pos++;

      if (this.source[pos] !== '=') {
        if (attrName) attrs[attrName] = '';
        continue;
      }
      pos++;

      while (pos < this.source.length && /\s/.test(this.source[pos])) pos++;

      let attrValue = '';
      const quote = this.source[pos];
      if (quote === '"' || quote === "'") {
        pos++;
        const valueStart = pos;
        while (pos < this.source.length && this.source[pos] !== quote) {
          pos++;
        }
        attrValue = this.source.slice(valueStart, pos);
        pos++;
      }

      if (attrName) attrs[attrName] = attrValue;
    }

    const isSelfClosing = this.source[pos] === '/';
    if (isSelfClosing) pos++;

    if (this.source[pos] !== '>') return null;

    const endPos = pos + 1;

    // Advance this.pos to after '>' before reading content,
    // so readUntilMatchingCloseTag starts from the content (depth=1 is correct).
    this.pos = endPos;

    let content = '';
    if (!isClosing && !isSelfClosing) {
      content = this.readUntilMatchingCloseTag(name);
      // this.pos is now past </tag>; leave it there.
    }

    return {
      name,
      attrs,
      content,
      isClosing,
      isSelfClosing,
    };
  }

  private readUntilMatchingCloseTag(tagName: string): string {
    const start = this.pos;
    const closeTag = `</${tagName}>`;
    let depth = 1;

    while (this.pos < this.source.length) {
      if (this.source[this.pos] === '<') {
        if (this.source.slice(this.pos, this.pos + 4) === '<!--') {
          const commentEnd = this.source.indexOf('-->', this.pos + 4);
          if (commentEnd === -1) {
            this.pos = this.source.length;
            break;
          }
          this.pos = commentEnd + 3;
          continue;
        }

        const isClose = this.source[this.pos + 1] === '/';
        if (isClose) {
          const closeCheck = this.source.slice(this.pos + 2, this.pos + 2 + tagName.length);
          if (closeCheck === tagName) {
            depth--;
            if (depth === 0) {
              const content = this.source.slice(start, this.pos);
              this.pos += closeTag.length;
              return content;
            }
            this.pos += closeTag.length;
            continue;
          }
        }

        const isSelfClosing = this.source.slice(this.pos, this.pos + 2).endsWith('/>');
        if (!isSelfClosing && this.source[this.pos + 1] !== '!') {
          let tagEnd = this.pos + 1;
          while (tagEnd < this.source.length && /[\w\-:]/.test(this.source[tagEnd])) {
            tagEnd++;
          }
          const innerTagName = this.source.slice(this.pos + 1, tagEnd);
          if (innerTagName === tagName) {
            depth++;
          }
        }
      }
      this.pos++;
    }

    return this.source.slice(start, this.pos);
  }

  private handleFencedBlock(): Token {
    // Skip the opening :::
    this.pos += 3;

    // Skip whitespace
    while (this.pos < this.source.length && this.source[this.pos] === ' ') {
      this.pos++;
    }

    // Parse the full directive line (e.g., "center", "float=left", "float=right width=50")
    const directive = this.readUntilNewline().trim();
    if (this.pos < this.source.length) {
      this.pos++; // skip newline
    }

    // Parse attributes from directive
    const align = this.parseFencedAttributes(directive);

    // Read content until we find closing :::
    let content = '';
    while (this.pos < this.source.length) {
      if (this.source[this.pos] === ':' && this.source.slice(this.pos, this.pos + 3) === ':::') {
        // Found closing :::
        this.pos += 3;
        break;
      }
      content += this.source[this.pos];
      this.pos++;
    }

    // Determine if this is an alignment or float block
    if (align.type === 'float') {
      return {
        type: 'float',
        align: align.align,
        width: align.width,
        content: content.trim(),
      };
    }

    // It's an alignment block
    return {
      type: 'alignment',
      align: align.align!,
      content: content.trim(),
    };
  }

  private parseFencedAttributes(directive: string): {
    type: 'alignment' | 'float';
    align?: 'left' | 'right' | 'center' | 'justify';
    width?: number;
  } {
    const parts = directive.split(/\s+/);
    const main = parts[0].toLowerCase();

    // Check for float=<left|right> syntax
    const floatMatch = /float\s*=\s*(left|right)/i.exec(directive);
    if (floatMatch) {
      const widthMatch = /width\s*=\s*(\d+)/i.exec(directive);
      return {
        type: 'float',
        align: floatMatch[1] as 'left' | 'right',
        width: widthMatch ? parseInt(widthMatch[1], 10) : undefined,
      };
    }

    // Check for align=<value> syntax
    const alignMatch = /align\s*=\s*(left|center|right|justify)/i.exec(directive);
    if (alignMatch) {
      return {
        type: 'alignment',
        align: alignMatch[1] as 'left' | 'center' | 'right' | 'justify',
      };
    }

    // Check for shorthand directives
    if (main === 'center' || main === 'left' || main === 'right' || main === 'justify') {
      return {
        type: 'alignment',
        align: main,
      };
    }

    if (main === 'float') {
      return { type: 'float', align: 'left' };
    }

    // Default to left alignment
    return { type: 'alignment', align: 'left' };
  }

  private handleLatex(): Token {
    // Only $$...$$ is supported for display math, single $ is treated as literal
    if (this.source.slice(this.pos, this.pos + 2) !== '$$') {
      return this.handleText();
    }

    this.pos += 2;
    const content = this.readUntil('$$');
    this.pos += 2;
    while (this.pos < this.source.length && this.source[this.pos] === ' ') {
      this.pos++;
    }
    return { type: 'latex', content: content.trim() };
  }

  private handleText(): Token {
    const content = this.readUntilNewline();
    this.inBlock = true;
    return { type: 'text', content: content.trim() };
  }

  private handleTextUntilNewline(): Token {
    const content = this.readUntilNewline();
    return { type: 'text', content };
  }

  private readUntilNewline(): string {
    const start = this.pos;
    while (
      this.pos < this.source.length &&
      this.source[this.pos] !== '\n' &&
      this.source[this.pos] !== '\r'
    ) {
      this.pos++;
    }
    return this.source.slice(start, this.pos);
  }

  private readUntil(terminator: string): string {
    const start = this.pos;
    while (this.pos < this.source.length) {
      if (this.source.slice(this.pos, this.pos + terminator.length) === terminator) {
        break;
      }
      this.pos++;
    }
    return this.source.slice(start, this.pos);
  }
}

export function tokenize(source: string): Token[] {
  return new Tokenizer().tokenize(source);
}
